import type { DataSession } from "../../../../data/core/session";
import { z } from "zod";

import type { RouterOutputs } from "@utils/trpc";

import { cachedSongs } from "@sqlite/schema";
import { songSchema } from "@libs/wire";
import { createLocalSongAssets } from "../localAudioPaths";
import chunk from "lodash/chunk";

export type CatalogSong = RouterOutputs["catalog"]["songs"]["getById"][number];

/**
 * Compile-time drift check between the inline router shape and the local
 * `songSchema` used by the durable entity cache. Same pattern as
 * `useSongs.ts` — keeps the cache from rejecting valid server data after
 * router or schema edits.
 */
type _SongRouterShape = CatalogSong;
type _SongSchemaShape = z.infer<typeof songSchema>;
type _SongSubset = _SongSchemaShape extends _SongRouterShape ? true : never;
type _SongSuperset = _SongRouterShape extends _SongSchemaShape ? true : never;
const _songDriftCheck: [_SongSubset, _SongSuperset] = [true, true];
void _songDriftCheck;

export function createSongsApi(data: DataSession) {
  const trpcVanillaClient = data.api;
  const withEntityCache = data.cache.withEntityCache;
  const { substituteLocalSongAssets } = createLocalSongAssets(data.db);
  const fetchFromCatalog = async (
    songIds: string[],
  ): Promise<CatalogSong[]> => {
    // Mirror the chunking strategy used by `hooks/trpc/useSongs.ts` so that a
    // playback queue resolved through this path doesn't blow past tRPC's
    // request-size limits when a long playlist is loaded by id.
    const batches = chunk(songIds, 20);
    const results: CatalogSong[] = [];
    for (let i = 0; i < batches.length; i += 5) {
      const slice = batches.slice(i, i + 5);
      const batchResults = await Promise.all(
        slice.map((batch) =>
          trpcVanillaClient.catalog.songs.getById.query(batch),
        ),
      );
      results.push(...batchResults.flat());
    }
    return results;
  };

  /**
   * Fetch songs by id for the playback path.
   *
   * Phase-2.6 cleanup: this used to wrap `fetchHandler + downloadManager.
   * getOfflineSongsById`, which did list-level merging of locally-downloaded
   * songs with the server response. Now the cached server response is the
   * single source of truth for what's IN the result, and the local
   * downloads table is only consulted to substitute `urlOnline` with a local
   * file path on a per-song basis (same pattern as image substitution in
   * `useLibrary`).
   */
  async function fetchSongsByIds(songIds: string[]): Promise<CatalogSong[]> {
    if (songIds.length === 0) return [];
    const songs = await withEntityCache<CatalogSong>({
      ids: songIds,
      entityTable: cachedSongs,
      idColumn: cachedSongs.songId,
      dataColumn: cachedSongs.data,
      fetch: fetchFromCatalog,
      schema: songSchema,
      getId: (s) => s.id,
      label: "playback/fetchSongsByIds",
    });
    return substituteLocalSongAssets(songs);
  }

  async function fetchSongById(songId: string): Promise<CatalogSong | null> {
    console.audio.log("fetchSongById", { songId });
    const songs = await fetchSongsByIds([songId]);
    const result = songs[0] ?? null;
    if (!result) {
      console.audio.warn("fetchSongById: song not found", { songId });
    }
    return result;
  }

  async function fetchFreshSongById(
    songId: string,
  ): Promise<CatalogSong | null> {
    console.audio.log("fetchFreshSongById", { songId });
    const songs = await fetchFromCatalog([songId]);
    const result = songs[0] ?? null;
    if (!result) {
      console.audio.warn("fetchFreshSongById: song not found", { songId });
    }
    return result;
  }

  return { fetchSongsByIds, fetchSongById, fetchFreshSongById };
}
